import React, { useState } from "react";
import { Table } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEditorPlugin } from "@udecode/plate/react";
import { TablePlugin } from "@udecode/plate-table/react";

interface TablePickerProps {
  onTableSelect: (rows: number, cols: number) => void;
}

const TablePicker: React.FC<TablePickerProps> = ({ onTableSelect }) => {
  const [hoveredCell, setHoveredCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const maxRows = 8;
  const maxCols = 8;

  const handleCellHover = (row: number, col: number) => {
    setHoveredCell({ row, col });
  };

  const handleCellClick = (row: number, col: number) => {
    onTableSelect(row + 1, col + 1);
  };

  const isHighlighted = (row: number, col: number) => {
    if (!hoveredCell) return false;
    return row <= hoveredCell.row && col <= hoveredCell.col;
  };

  return (
    <div className="p-4">
      <div className="mb-2 text-sm text-gray-600 text-center">
        {hoveredCell
          ? `${hoveredCell.row + 1} x ${hoveredCell.col + 1}`
          : "Select table size"}
      </div>
      <div className="grid grid-cols-8 gap-1">
        {Array.from({ length: maxRows * maxCols }).map((_, index) => {
          const row = Math.floor(index / maxCols);
          const col = index % maxCols;
          return (
            <div
              key={index}
              className={`
                w-4 h-4 border border-gray-300 cursor-pointer transition-colors
                ${
                  isHighlighted(row, col)
                    ? "bg-blue-500 border-blue-600"
                    : "bg-white hover:bg-gray-100"
                }
              `}
              onMouseEnter={() => handleCellHover(row, col)}
              onClick={() => handleCellClick(row, col)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const TableDropdownMenu: React.FC = () => {
  const { editor, tf } = useEditorPlugin(TablePlugin);
  const [isOpen, setIsOpen] = useState(false);

  const handleTableSelect = (rows: number, cols: number) => {
    if (editor) {
      // Use Plate's built-in table insertion method
      tf.insert.table({
        rowCount: rows,
        colCount: cols,
        header: true, // First row will be headers
      });
    }

    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          title="Insert table"
        >
          <Table className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-0">
        <TablePicker onTableSelect={handleTableSelect} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
